import React, { useState, DragEvent } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  FireIcon,
  BeakerIcon,
  CakeIcon,
  SparklesIcon,
  PrinterIcon,
  ArrowRightIcon,
  LinkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface MenuItem {
  id: number;
  name: string;
  category: string;
}

interface Printer {
  id: number;
  name: string;
  ipAddress: string;
  isActive: boolean;
}

interface Kitchen {
  id: number;
  name: string;
  type: string;
  isActive: boolean;
  printer?: Printer;
  menuItems: MenuItem[];
}

interface KitchenVisualMapProps {
  kitchens: Kitchen[];
  onKitchenClick?: (kitchen: Kitchen) => void;
  onPrinterClick?: (printer: Printer) => void;
  onMenuItemClick?: (item: MenuItem, kitchen: Kitchen) => void;
  onLinkPrinter?: (kitchen: Kitchen) => void;
  onUnlinkPrinter?: (kitchen: Kitchen) => void;
  onManageItems?: (kitchen: Kitchen) => void;
  onMenuItemDrop?: (item: MenuItem, fromKitchen: Kitchen, toKitchen: Kitchen) => void;
}

const KitchenVisualMap: React.FC<KitchenVisualMapProps> = ({
  kitchens,
  onKitchenClick,
  onPrinterClick,
  onMenuItemClick,
  onLinkPrinter,
  onUnlinkPrinter,
  onManageItems,
  onMenuItemDrop
}) => {
  const { theme } = useTheme();
  const [expandedKitchens, setExpandedKitchens] = useState<Set<number>>(new Set());
  const [draggedItem, setDraggedItem] = useState<{item: MenuItem, kitchen: Kitchen} | null>(null);
  const [dragOverKitchen, setDragOverKitchen] = useState<number | null>(null);

  const toggleKitchen = (kitchenId: number) => {
    const newExpanded = new Set(expandedKitchens);
    if (newExpanded.has(kitchenId)) {
      newExpanded.delete(kitchenId);
    } else {
      newExpanded.add(kitchenId);
    }
    setExpandedKitchens(newExpanded);
  };

  const getKitchenIcon = (type: string) => {
    switch (type) {
      case 'MAIN': return <FireIcon className="h-6 w-6" />;
      case 'COLD': return <BeakerIcon className="h-6 w-6" />;
      case 'DESSERT': return <CakeIcon className="h-6 w-6" />;
      default: return <SparklesIcon className="h-6 w-6" />;
    }
  };

  const getKitchenColor = (type: string) => {
    switch (type) {
      case 'MAIN': return '#ef4444';
      case 'COLD': return '#3b82f6';
      case 'DESSERT': return '#f59e0b';
      default: return '#10b981';
    }
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, item: MenuItem, kitchen: Kitchen) => {
    setDraggedItem({ item, kitchen });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (kitchenId: number) => {
    setDragOverKitchen(kitchenId);
  };

  const handleDragLeave = () => {
    setDragOverKitchen(null);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, toKitchen: Kitchen) => {
    e.preventDefault();
    setDragOverKitchen(null);
    
    if (draggedItem && draggedItem.kitchen.id !== toKitchen.id) {
      onMenuItemDrop?.(draggedItem.item, draggedItem.kitchen, toKitchen);
    }
    
    setDraggedItem(null);
  };

  return (
    <div className="w-full space-y-6">
      {/* Visual Map Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: theme.text }}>
          Kitchen Operations Map
        </h2>
        <p className="text-sm" style={{ color: theme.gray?.[600] }}>
          Visual representation of kitchen workflows and connections
        </p>
      </div>

      {/* Kitchen Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {kitchens.map((kitchen) => (
          <div
            key={kitchen.id}
            className={`relative rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl ${
              dragOverKitchen === kitchen.id ? 'ring-4' : ''
            }`}
            style={{
              backgroundColor: theme.components?.card?.background,
              border: `2px solid ${kitchen.isActive ? getKitchenColor(kitchen.type) : theme.gray?.[300]}`,
              ringColor: dragOverKitchen === kitchen.id ? theme.primary : 'transparent'
            }}
            onDragOver={handleDragOver}
            onDragEnter={() => handleDragEnter(kitchen.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, kitchen)}
          >
            {/* Kitchen Header */}
            <div 
              className="p-4 cursor-pointer"
              style={{
                backgroundColor: kitchen.isActive ? getKitchenColor(kitchen.type) + '10' : theme.gray?.[100]
              }}
              onClick={() => onKitchenClick?.(kitchen)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{
                      backgroundColor: kitchen.isActive ? getKitchenColor(kitchen.type) + '20' : theme.gray?.[200],
                      color: kitchen.isActive ? getKitchenColor(kitchen.type) : theme.gray?.[500]
                    }}
                  >
                    {getKitchenIcon(kitchen.type)}
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: theme.text }}>
                      {kitchen.name}
                    </h3>
                    <p className="text-sm" style={{ color: theme.gray?.[600] }}>
                      {kitchen.type} • {kitchen.menuItems.length} items
                    </p>
                  </div>
                </div>
                {!kitchen.isActive && (
                  <ExclamationTriangleIcon 
                    className="h-5 w-5" 
                    style={{ color: theme.warning }}
                    title="Kitchen is inactive"
                  />
                )}
              </div>
            </div>

            {/* Printer Connection */}
            <div className="px-4 py-3 border-t" style={{ borderColor: theme.components?.card?.border }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PrinterIcon className="h-5 w-5" style={{ color: theme.gray?.[500] }} />
                  {kitchen.printer ? (
                    <div 
                      className="flex items-center gap-2 cursor-pointer hover:underline"
                      onClick={() => onPrinterClick?.(kitchen.printer!)}
                    >
                      <span className="text-sm font-medium" style={{ color: theme.text }}>
                        {kitchen.printer.name}
                      </span>
                      {!kitchen.printer.isActive && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                          Offline
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm" style={{ color: theme.gray?.[500] }}>
                      No printer connected
                    </span>
                  )}
                </div>
                
                {kitchen.printer ? (
                  onUnlinkPrinter && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnlinkPrinter(kitchen);
                      }}
                      className="text-xs px-2 py-1 rounded transition-colors"
                      style={{
                        backgroundColor: theme.danger + '20',
                        color: theme.danger
                      }}
                    >
                      Unlink
                    </button>
                  )
                ) : (
                  onLinkPrinter && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onLinkPrinter(kitchen);
                      }}
                      className="text-xs px-2 py-1 rounded transition-colors"
                      style={{
                        backgroundColor: theme.primary + '20',
                        color: theme.primary
                      }}
                    >
                      <LinkIcon className="h-3 w-3 inline mr-1" />
                      Link Printer
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Menu Items Section */}
            <div className="px-4 py-3 border-t" style={{ borderColor: theme.components?.card?.border }}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium" style={{ color: theme.text }}>
                  Assigned Menu Items
                </h4>
                {onManageItems && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageItems(kitchen);
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Manage
                  </button>
                )}
              </div>
              
              {/* Menu Items Preview */}
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {kitchen.menuItems.slice(0, expandedKitchens.has(kitchen.id) ? undefined : 3).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 p-1.5 rounded cursor-move hover:bg-gray-50"
                    draggable={onMenuItemDrop !== undefined}
                    onDragStart={(e) => handleDragStart(e, item, kitchen)}
                    onClick={() => !draggedItem && onMenuItemClick?.(item, kitchen)}
                    style={{
                      opacity: draggedItem?.item.id === item.id ? 0.5 : 1
                    }}
                  >
                    <ArrowRightIcon className="h-3 w-3" style={{ color: theme.gray?.[400] }} />
                    <span className="text-xs" style={{ color: theme.text }}>{item.name}</span>
                    <span className="text-xs" style={{ color: theme.gray?.[500] }}>
                      ({item.category})
                    </span>
                  </div>
                ))}
                
                {kitchen.menuItems.length > 3 && !expandedKitchens.has(kitchen.id) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleKitchen(kitchen.id);
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    +{kitchen.menuItems.length - 3} more items
                  </button>
                )}
              </div>

              {kitchen.menuItems.length === 0 && (
                <p className="text-xs text-center py-2" style={{ color: theme.gray?.[500] }}>
                  No items assigned
                </p>
              )}
            </div>

            {/* Visual Flow Indicator */}
            {kitchen.printer && kitchen.menuItems.length > 0 && (
              <div 
                className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: theme.success + '20',
                  color: theme.success
                }}
              >
                ✓ Ready
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {kitchens.length === 0 && (
        <div className="text-center py-12">
          <FireIcon className="h-16 w-16 mx-auto mb-4" style={{ color: theme.gray?.[400] }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: theme.text }}>
            No Kitchens Configured
          </h3>
          <p className="text-sm" style={{ color: theme.gray?.[600] }}>
            Start by adding your kitchen stations
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="mt-8 p-4 rounded-lg" style={{ backgroundColor: theme.components?.card?.background }}>
        <h4 className="text-sm font-medium mb-3" style={{ color: theme.text }}>Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span style={{ color: theme.gray?.[600] }}>Ready (Active with printer & items)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span style={{ color: theme.gray?.[600] }}>Inactive Kitchen</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span style={{ color: theme.gray?.[600] }}>Offline Printer</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400" />
            <span style={{ color: theme.gray?.[600] }}>No Configuration</span>
          </div>
        </div>
        {onMenuItemDrop && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: theme.components?.card?.border }}>
            <p className="text-xs" style={{ color: theme.gray?.[600] }}>
              💡 Tip: Drag and drop menu items between kitchens to reassign them
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KitchenVisualMap;